/**
 * Connection Status Component - Phase 5: Real-time Collaboration
 * Displays WebSocket connection status with visual indicator
 */

import React from 'react';
import { useWebSocketContext } from '@/contexts/WebSocketContext';
import { Wifi, WifiOff, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';

interface ConnectionStatusProps {
  showText?: boolean;
  className?: string;
}

export function ConnectionStatus({ showText = true, className }: ConnectionStatusProps) {
  const { isConnected, isConnecting, error } = useWebSocketContext();

  // Check if WebSocket is disabled
  const isWebSocketDisabled = import.meta.env.VITE_DISABLE_WEBSOCKET === 'true';

  // If WebSocket is disabled, don't show the connection status
  if (isWebSocketDisabled) {
    return null;
  }

  // Determine status
  const status = isConnected ? 'connected' : isConnecting ? 'connecting' : 'disconnected';

  // Status configurations
  const statusConfig = {
    connected: {
      icon: Wifi,
      text: 'Connected',
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      dotColor: 'bg-green-500',
      title: 'Real-time connection active',
    },
    connecting: {
      icon: Loader2,
      text: 'Connecting',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
      dotColor: 'bg-yellow-500',
      title: 'Establishing real-time connection...',
    },
    disconnected: {
      icon: WifiOff,
      text: 'Disconnected',
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      dotColor: 'bg-red-500',
      title: error || 'Real-time connection unavailable',
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <div
      className={clsx(
        'flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-200',
        config.bgColor,
        className
      )}
      title={config.title}
    >
      {/* Pulsing dot indicator */}
      <div className="relative flex items-center justify-center">
        <span
          className={clsx(
            'absolute inline-flex h-3 w-3 rounded-full opacity-75',
            config.dotColor,
            status === 'connected' && 'animate-ping'
          )}
        />
        <span className={clsx('relative inline-flex rounded-full h-2 w-2', config.dotColor)} />
      </div>

      {/* Icon */}
      <Icon
        className={clsx('h-4 w-4', config.color, status === 'connecting' && 'animate-spin')}
      />

      {/* Status text (optional) */}
      {showText && (
        <span className={clsx('text-sm font-medium', config.color)}>{config.text}</span>
      )}
    </div>
  );
}

// Compact version for mobile/small spaces
export function ConnectionStatusCompact() {
  return <ConnectionStatus showText={false} className="px-2 py-1" />;
}

// Detailed version with additional info
export function ConnectionStatusDetailed() {
  const { isConnected, isConnecting, error, socket } = useWebSocketContext();

  // Check if WebSocket is disabled
  const isWebSocketDisabled = import.meta.env.VITE_DISABLE_WEBSOCKET === 'true';

  if (isWebSocketDisabled) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 p-4 rounded-lg  border border-white/[0.06]">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-white/80">Real-time Connection</span>
        <ConnectionStatus showText={false} />
      </div>

      <div className="text-xs text-white/70 space-y-1">
        {isConnected && socket && (
          <>
            <div>Socket ID: {socket.id}</div>
            <div className="text-green-600">✓ Receiving live updates</div>
          </>
        )}

        {isConnecting && <div className="text-yellow-600">⏳ Establishing connection...</div>}

        {!isConnected && !isConnecting && (
          <>
            <div className="text-red-600">✗ Connection unavailable</div>
            {error && <div className="text-red-500 text-xs mt-1">Error: {error}</div>}
          </>
        )}
      </div>
    </div>
  );
}
